import { Select } from "antd";
import { getSatinColumnColor } from "./satinPalette";

// Keep the inspector the same size as columns are added. Pair indices remain
// indices into the original array, including any unfinished edge pairs.
export default function SatinColumnControls({
  pairs = [],
  selectedPairIdx,
  onSelect,
  onAddGuide,
  onEditEdge,
  onClearGuides,
  onClearAll,
}) {
  const columns = pairs
    .map((pair, pairIdx) => ({ pair, pairIdx }))
    .filter(({ pair }) => pair?.rail0 && pair?.rail1);
  const selected =
    columns.find(({ pairIdx }) => pairIdx === selectedPairIdx) || columns[0];
  if (!selected) return null;

  const { pair, pairIdx } = selected;
  const color = getSatinColumnColor(pairIdx);
  const guideCount = pair.rungs?.length || 0;
  const buttonStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: "7px",
    background: "#fff",
    color: "#4b5563",
    padding: "8px",
    minHeight: "36px",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: "inherit",
  };

  return (
    <section
      aria-label="Satin columns"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "9px",
        padding: "12px",
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        background: "#fafafa",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "12px", fontWeight: 700, color: "#374151" }}>
          Columns
        </span>
        <span style={{ fontSize: "11px", color: "#6b7280" }}>
          {columns.length} total
        </span>
      </div>
      <Select
        aria-label="Satin column"
        value={pairIdx}
        onChange={(value) => onSelect?.(value)}
        size="large"
        virtual={false}
        listHeight={240}
        style={{ width: "100%", minWidth: 0 }}
        dropdownStyle={{ fontFamily: "Clash Grotesk, sans-serif" }}
        options={columns.map(({ pairIdx: index }) => {
          const optionColor = getSatinColumnColor(index);
          return {
            value: index,
            "aria-label": `Column ${index + 1}`,
            label: (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "9px",
                  color: optionColor.text,
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: optionColor.stroke,
                    flexShrink: 0,
                  }}
                />
                Column {index + 1}
              </span>
            ),
          };
        })}
      />
      <span style={{ fontSize: "10px", color: "#6b7280", lineHeight: 1.4 }}>
        Matches the numbered color marker on the canvas.
      </span>
      <button
        onClick={() => onAddGuide?.(pairIdx)}
        style={{
          ...buttonStyle,
          borderColor: color.border,
          color: color.text,
          background: color.soft,
        }}
      >
        + Add direction guide
      </button>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}
      >
        {[0, 1].map((edge) => (
          <button
            key={edge}
            onClick={() => onEditEdge?.(pairIdx, edge)}
            style={buttonStyle}
          >
            Edit edge {edge + 1}
          </button>
        ))}
      </div>
      <span style={{ fontSize: "10px", color: "#6b7280", lineHeight: 1.4 }}>
        {guideCount} direction {guideCount === 1 ? "guide" : "guides"}
        {guideCount < 3
          ? " · Use at least 3 on curves."
          : " · Direction is constrained."}
      </span>
      <details>
        <summary
          style={{
            fontSize: "10px",
            color: "#6b7280",
            cursor: "pointer",
            padding: "4px 0",
          }}
        >
          More actions
        </summary>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginTop: "6px",
          }}
        >
          {guideCount > 0 && (
            <button
              onClick={() => onClearGuides?.(pairIdx)}
              style={buttonStyle}
            >
              Clear guides for Column {pairIdx + 1}
            </button>
          )}
          <button
            onClick={onClearAll}
            style={{ ...buttonStyle, color: "#b91c1c" }}
          >
            Clear all satin edges
          </button>
        </div>
      </details>
    </section>
  );
}
